// Issue #87: Notifications list page
// Issue #131: Delete notifications feature
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './NotificationList.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu } from './shared';
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function NotificationList() {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // Issue #131: Selection mode state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const announcementRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setIsLoading(true);
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/notifications`);
      setNotifications(response.data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      if (isInitialLoad) {
        setError(err.response?.data?.message || t('notifications.errorLoading'));
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, [t]);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications(true);

    // Poll for new notifications every 5 seconds
    const pollInterval = setInterval(() => {
      fetchNotifications(false);
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [fetchNotifications]);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Show relative time for recent notifications
    if (diffMins < 1) {
      return t('notifications.justNow');
    } else if (diffMins < 60) {
      return `${diffMins} ${t('notifications.minutesAgo')}`;
    } else if (diffHours < 24) {
      return `${diffHours} ${t('notifications.hoursAgo')}`;
    } else if (diffDays < 7) {
      return `${diffDays} ${t('notifications.daysAgo')}`;
    }

    // Show date for older notifications
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format notification body based on language and notification type
  // Issue #87: Use only first name and display it bold for comment/chat notifications
  const formatNotificationBody = (notification) => {
    const { notification_type, actor_name, reference_title, body } = notification;

    // Try to extract title from body if reference_title is not set (for older notifications)
    // Titles are enclosed in quotes like "Title here"
    let title = reference_title;
    if (!title && body) {
      const match = body.match(/"([^"]+)"/);
      if (match) {
        title = match[1];
      }
    }

    // Issue #87: Extract only first name from actor_name
    const firstName = actor_name ? actor_name.split(' ')[0] : '';

    switch (notification_type) {
      case 'comment':
        if (actor_name === 'Anonym' || actor_name === 'Anonymous') {
          return t('notifications.commentMessageAnonymous').replace('{title}', title || '');
        }
        // Issue #87: Use only first name and make it bold
        const commentParts = t('notifications.commentMessage').split('{actor}');
        const afterActorParts = commentParts[1].split('{title}');
        return (
          <>
            {commentParts[0]}
            <strong>{firstName}</strong>
            {afterActorParts[0]}
            {title || ''}
            {afterActorParts[1]}
          </>
        );
      case 'like':
        return t('notifications.likeMessage').replace('{title}', title || '');
      case 'heart':
        return t('notifications.heartMessage').replace('{title}', title || '');
      case 'chat':
        // Issue #87: Use only first name and make it bold
        const chatParts = t('notifications.chatMessage').split('{actor}');
        return (
          <>
            {chatParts[0]}
            <strong>{firstName}</strong>
            {chatParts[1]}
          </>
        );
      case 'favorite_sold':
        return t('notifications.favoriteSoldMessage').replace('{title}', title || '');
      default:
        return body;
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'comment':
        return (
          <svg className="notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'like':
        return (
          <svg className="notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V13C2 12.4696 2.21071 11.9609 2.58579 11.5858C2.96086 11.2107 3.46957 11 4 11H7M14 9V5C14 4.20435 13.6839 3.44129 13.1213 2.87868C12.5587 2.31607 11.7956 2 11 2L7 11V22H18.28C18.7623 22.0055 19.2304 21.8364 19.5979 21.524C19.9654 21.2116 20.2077 20.7769 20.28 20.3L21.66 11.3C21.7035 11.0134 21.6842 10.7207 21.6033 10.4423C21.5225 10.1638 21.3821 9.90629 21.1919 9.68751C21.0016 9.46873 20.7661 9.29393 20.5016 9.17522C20.2371 9.0565 19.9499 8.99672 19.66 9H14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'heart':
        return (
          <svg className="notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'chat':
        return (
          <svg className="notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'favorite_sold':
        return (
          <svg className="notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default:
        return (
          <svg className="notification-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
    }
  };

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await axios.put(`${API_BASE_URL}/api/notifications/${notification.id}/read`);
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }

    // Navigate based on reference type
    if (notification.reference_type && notification.reference_id) {
      switch (notification.reference_type) {
        case 'post':
          // Issue #87: Open comments section if this is a comment notification
          const openComments = notification.notification_type === 'comment';
          navigation.onNavigateToPost(notification.reference_id, openComments);
          break;
        case 'marketplace':
          // Issue #87: Navigate to appropriate tab based on notification type
          if (notification.notification_type === 'favorite_sold') {
            navigation.onNavigateToMeinMarktplatz('favorites');
          } else if (notification.notification_type === 'heart') {
            navigation.onNavigateToMeinMarktplatz('myObjects');
          } else {
            navigation.onNavigateToMarketplaceDetail(notification.reference_id);
          }
          break;
        case 'conversation':
          navigation.onNavigateToChat(notification.reference_id);
          break;
        default:
          console.log('Unknown reference type:', notification.reference_type);
      }
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await axios.put(`${API_BASE_URL}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      // Dispatch event to notify BottomNavigation to update red dot immediately
      window.dispatchEvent(new CustomEvent('notificationsMarkedRead'));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Issue #131: Announce to screen readers
  const announce = (message) => {
    if (announcementRef.current) {
      announcementRef.current.textContent = message;
      // Clear after announcement
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent = '';
        }
      }, 1000);
    }
  };

  // Issue #131: Long press handlers
  const handleLongPressStart = (notificationId) => {
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      if (!isSelectionMode) {
        setIsSelectionMode(true);
        setSelectedNotifications(new Set([notificationId]));
        announce(t('notifications.selectionMode'));
      }
    }, 500); // 500ms for long press
  };

  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Issue #131: Toggle notification selection
  const toggleNotificationSelection = (notificationId) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
        announce(t('notifications.notificationDeselected'));
      } else {
        newSet.add(notificationId);
        announce(t('notifications.notificationSelected'));
      }
      return newSet;
    });
  };

  // Issue #131: Select all notifications
  const handleSelectAll = () => {
    const allIds = new Set(notifications.map(n => n.id));
    setSelectedNotifications(allIds);
    announce(`${notifications.length} ${t('notifications.selected')}`);
  };

  // Issue #131: Exit selection mode
  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedNotifications(new Set());
    announce(t('notifications.exitSelectionMode'));
  };

  // Issue #131: Delete selected notifications
  const handleDeleteSelected = async () => {
    if (selectedNotifications.size === 0) return;

    setIsDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/notifications`, {
        data: { notification_ids: Array.from(selectedNotifications) }
      });

      // Remove deleted notifications from local state
      setNotifications(prev => prev.filter(n => !selectedNotifications.has(n.id)));
      announce(t('notifications.deleteSuccess'));

      // Exit selection mode
      setIsSelectionMode(false);
      setSelectedNotifications(new Set());

      // Dispatch event to update unread count in BottomNavigation
      window.dispatchEvent(new CustomEvent('notificationsMarkedRead'));
    } catch (err) {
      console.error('Failed to delete notifications:', err);
      setError(t('notifications.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Issue #131: Handle keyboard for selection mode
  const handleNotificationKeyDown = (e, notification) => {
    if (isSelectionMode) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleNotificationSelection(notification.id);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        exitSelectionMode();
      }
    } else {
      // Shift+Enter to enter selection mode (accessibility)
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        setIsSelectionMode(true);
        setSelectedNotifications(new Set([notification.id]));
        announce(t('notifications.selectionMode'));
      }
    }
  };

  const hasUnreadNotifications = notifications.some(n => !n.is_read);

  if (isLoading) {
    return (
      <div className="notification-list-page">
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        />
        <div className="tab-navigation-notification-list">
          <span className="tab-title">{t('notifications.title')}</span>
        </div>
        <main className="notification-list-content">
          <div className="loading-container">
            <span>{t('notifications.loading')}</span>
          </div>
        </main>
        <BottomNavigation
          activeTab="notifications"
          onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
        />
      </div>
    );
  }

  return (
    <div className="notification-list-page">
      {/* Header Navigation Bar */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-notification-list">
        <span className="tab-title">{t('notifications.title')}</span>
      </div>

      {/* Issue #131: Screen reader announcements */}
      <div
        ref={announcementRef}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      ></div>

      {/* Content */}
      <main className="notification-list-content">
        {/* Issue #131: Selection mode action bar */}
        {isSelectionMode ? (
          <div className="selection-action-bar" role="toolbar" aria-label={t('notifications.selectionMode')}>
            <button
              className="select-all-btn"
              onClick={handleSelectAll}
              aria-label={t('notifications.selectAll')}
            >
              {t('notifications.selectAll')}
            </button>
            <button
              className="delete-selected-btn"
              onClick={handleDeleteSelected}
              disabled={selectedNotifications.size === 0 || isDeleting}
              aria-label={`${t('notifications.deleteSelected')} (${selectedNotifications.size} ${t('notifications.selected')})`}
            >
              {isDeleting ? (
                <span className="deleting-spinner" aria-hidden="true"></span>
              ) : (
                <svg className="delete-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
            <button
              className="exit-selection-btn"
              onClick={exitSelectionMode}
              aria-label={t('notifications.exitSelectionMode')}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        ) : (
          hasUnreadNotifications && (
            <button className="mark-all-read-btn" onClick={handleMarkAllAsRead}>
              {t('notifications.markAllRead')}
            </button>
          )
        )}

        {/* Issue #115: Added role="alert" for screen reader announcement */}
        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        {/* Issue #115: Added aria-live region for dynamic content */}
        {notifications.length === 0 && !error ? (
          <div className="empty-state" role="status">
            <svg className="empty-state-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="empty-title">{t('notifications.noNotifications')}</span>
            <span className="empty-hint">{t('notifications.noNotificationsHint')}</span>
          </div>
        ) : (
          <div
            className="notifications-list"
            role="list"
            aria-label={t('notifications.title') || 'Benachrichtigungen'}
            aria-live="polite"
          >
            {notifications.map((notification) => {
              const isSelected = selectedNotifications.has(notification.id);
              return (
                <button
                  key={notification.id}
                  className={`notification-card ${!notification.is_read ? 'unread' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    if (isSelectionMode) {
                      toggleNotificationSelection(notification.id);
                    } else if (!longPressTriggeredRef.current) {
                      handleNotificationClick(notification);
                    }
                  }}
                  onMouseDown={() => handleLongPressStart(notification.id)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={() => handleLongPressStart(notification.id)}
                  onTouchEnd={handleLongPressEnd}
                  onTouchCancel={handleLongPressEnd}
                  onKeyDown={(e) => handleNotificationKeyDown(e, notification)}
                  aria-label={`${isSelected ? t('notifications.selected') + ': ' : ''}${!notification.is_read ? (t('notifications.unread') || 'Ungelesen') + ': ' : ''}${notification.body || ''} - ${formatTimestamp(notification.created_at)}`}
                  aria-pressed={isSelectionMode ? isSelected : undefined}
                  role={isSelectionMode ? 'checkbox' : 'button'}
                  aria-checked={isSelectionMode ? isSelected : undefined}
                >
                  {/* Issue #131: Selection checkbox icon or Notification Icon */}
                  <div className="notification-icon-container" aria-hidden="true">
                    {isSelected ? (
                      <svg className="check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      getNotificationIcon(notification.notification_type)
                    )}
                  </div>

                  {/* Notification Info */}
                  <div className="notification-info">
                    <div className="notification-header">
                      <span className="notification-body">{formatNotificationBody(notification)}</span>
                    </div>
                    <span className="notification-timestamp">
                      {formatTimestamp(notification.created_at)}
                    </span>
                  </div>

                  {/* Unread Indicator - hide when selected */}
                  {!notification.is_read && !isSelected && (
                    <div className="unread-indicator" aria-hidden="true"></div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </main>

      {/* Bottom Navigation Bar */}
      <BottomNavigation
        activeTab="notifications"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
    </div>
  );
}

export default NotificationList;
